import { useEffect, useState, PropsWithChildren, ComponentProps } from 'react';
import { Toast } from '@streamelements/frontend-ui';

interface ToastType {
  message: string;
  color: ComponentProps<typeof Toast.Root>['color'];
  placementOffset?: number;
  sideOffset?: number;
  id?: number;
  duration?: number;
}

type ToastWithId = ToastType & { id: number; isOpen: boolean };

let toasts = [] as ToastWithId[];

const listeners = new Set<CallableFunction>();

function getState() {
  return toasts;
}

function updateState(newState: ToastWithId[] | ((prev: ToastWithId[]) => ToastWithId[])) {
  toasts = typeof newState === 'function' ? newState(toasts) : newState;
  listeners.forEach((listener) => listener(toasts));
}

function subscribe(listener: (param: ToastWithId[]) => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * @returns {Function} A function that can be used to show a toast.
 * The return value is a function that can be used to remove the toast.
 */
export function showToast(toast: ToastType) {
  const id = Date.now();
  const newItem = { ...toast, id, duration: toast.duration ?? 3000, isOpen: true };
  updateState((prev) => [...prev, newItem]);
  return () => updateState((prev) => prev.filter((toast) => toast.id !== id));
}

/**
 * @returns {Function} A function that can be used to show a toast.
 * The return value is a function that can be used to remove the toast.
 */
export function useShowToast() {
  return showToast;
}

/**
 * @returns {Array} A tuple with the toasts and the showToast function.
 */
function useToasts() {
  const [toasts, setToasts] = useState(() => getState());

  useEffect(() => {
    return subscribe((newExternalState) => {
      setToasts(newExternalState);
    });
  }, []);

  const showToast = useShowToast();

  return [toasts, showToast] as const;
}

/**
 * Usage: Just import `useShowToast` hook and do `const showToast = useShowToast();`
 * Then you can do `showToast({ color: 'success' | 'error' | 'attention' | 'info' | 'neutral', message: 'Some message!' });`
 */
export function Toasts({ children }: PropsWithChildren<unknown>) {
  const [toasts] = useToasts();

  return (
    <Toast.Provider duration={3000}>
      {children}
      {toasts.map((toast) => (
        <Toast.Root
          key={toast.id}
          onOpenChange={(next: boolean) => {
            if (!next) {
              updateState((prev) => {
                return prev.map((item) => (item.id === toast.id ? { ...item, isOpen: false } : item));
              });
            }
          }}
          duration={toast.duration ?? 5000}
          color={toast.color}
          style={{ marginBottom: toast.sideOffset }}
          css={{ padding: '0.5rem 0.5rem', minHeight: 'initial', br: '0.5rem' }}
        >
          <Toast.Title css={{ fontSize: '$bodyText' }}>{toast.message}</Toast.Title>
        </Toast.Root>
      ))}
      <Toast.Viewport />
    </Toast.Provider>
  );
}
